import { useCallback, useEffect, useState } from 'react'
import { Box, IconButton, Stack, Typography } from '@mui/material'
import {
  ArrowUturnLeftIcon,
  CheckCircleIcon,
  CheckIcon,
  PencilSquareIcon,
  TrashIcon,
  XMarkIcon
} from '@heroicons/react/24/outline'

import { GoalStatus, type UserGoal } from '../../../../../../../pure-js/types/CoachTypes'
import { INTERAX_COLORS } from '../../../../../constants/colors'
import { TESTIDS } from '../../../../../config/testids'
import { useTranslation } from '../../../../../shared/hooks/useIntractionTranslate'
import { MAX_GOAL_LENGTH_CHARS } from '../../../../../../../pure-js/libs/AICoachStateHelper'
import { Input } from '../../../../../interaxDesignSystemComponents'
import { GOAL_SOURCE_KEY } from '../coachPageStyles'

type CoachGoalRowProps = {
  goal: UserGoal
  /** Nothing can be changed while another goal's edit or delete is still in flight. */
  disabled: boolean
  onSave: (goal: UserGoal, text: string) => void
  onRequestDelete: (goal: UserGoal) => void
  /** Move a goal between active and achieved. Achieving one frees a slot; reopening one takes it back. */
  onSetStatus: (goal: UserGoal, status: GoalStatus) => void
  /** Why this goal's last change was refused, shown on the row the click came from. */
  failureMessage?: string
}

/** One goal, readable and correctable in place — Aria's wording is often close but not the person's own. */
export const CoachGoalRow = ({
  goal,
  disabled,
  onSave,
  onRequestDelete,
  onSetStatus,
  failureMessage
}: CoachGoalRowProps) => {
  const { t } = useTranslation()
  const [isEditing, setIsEditing] = useState(false)
  const [draft, setDraft] = useState(goal.goal)

  // A landed save replaces what is in the box, but never mid-edit — that would wipe typing with no undo.
  useEffect(() => {
    if (isEditing) return
    setDraft(goal.goal)
  }, [goal.goal, isEditing])

  const startEditing = useCallback(() => {
    setDraft(goal.goal)
    setIsEditing(true)
  }, [goal.goal])

  const cancelEditing = useCallback(() => {
    setDraft(goal.goal)
    setIsEditing(false)
  }, [goal.goal])

  // Enter goes through the same gate as the tick — the input is never disabled, so it could double-fire.
  const commit = useCallback(() => {
    if (disabled) return
    const text = draft.trim()
    setIsEditing(false)
    if (!text || text === goal.goal) {
      setDraft(goal.goal)
      return
    }
    onSave(goal, text)
  }, [disabled, draft, goal, onSave])

  if (isEditing) {
    return (
      <Box sx={goalRowSx} data-testid={TESTIDS.COACH_GOAL_ROW}>
        <Input
          fullWidth
          autoFocus
          multiline
          maxRows={GOAL_EDIT_MAX_ROWS}
          size="small"
          value={draft}
          maxLength={MAX_GOAL_LENGTH_CHARS}
          onChange={(event) => setDraft(event.target.value)}
          onEnterPress={commit}
          placeholder={t('gamer.ariaCoach.goalEditPlaceholder')}
          data-testid={TESTIDS.COACH_GOAL_EDIT_INPUT}
        />
        <Stack direction="row" spacing={0.5} justifyContent="flex-end" sx={{ mt: 1 }}>
          <IconButton
            size="small"
            onClick={cancelEditing}
            aria-label={t('gamer.ariaCoach.goalEditCancel')}
            sx={{ color: INTERAX_COLORS.thunderGrey }}
          >
            <XMarkIcon width={18} />
          </IconButton>
          <IconButton
            size="small"
            onClick={commit}
            disabled={disabled || !draft.trim()}
            aria-label={t('gamer.ariaCoach.goalEditSave')}
            data-testid={TESTIDS.COACH_GOAL_SAVE_BTN}
            sx={{ color: INTERAX_COLORS.sun, '&.Mui-disabled': { color: INTERAX_COLORS.white30 } }}
          >
            <CheckIcon width={18} />
          </IconButton>
        </Stack>
      </Box>
    )
  }

  const isAchieved = goal.status === GoalStatus.ACHIEVED

  return (
    <Box sx={goalRowSx} data-testid={TESTIDS.COACH_GOAL_ROW}>
      <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              color: isAchieved ? INTERAX_COLORS.thunderGrey : INTERAX_COLORS.white,
              fontSize: 14,
              textDecoration: isAchieved ? 'line-through' : 'none'
            }}
          >
            {goal.goal}
          </Typography>
          <Typography sx={{ color: INTERAX_COLORS.thunderGrey, fontSize: 12, mt: 0.5 }}>
            {isAchieved ? t('gamer.ariaCoach.goalAchieved') : t(GOAL_SOURCE_KEY[goal.source])}
          </Typography>
        </Box>
        <Stack direction="row" spacing={0.25} sx={{ flexShrink: 0 }}>
          <IconButton
            size="small"
            onClick={() => onSetStatus(goal, isAchieved ? GoalStatus.ACTIVE : GoalStatus.ACHIEVED)}
            disabled={disabled}
            aria-label={isAchieved ? t('gamer.ariaCoach.goalReopen') : t('gamer.ariaCoach.goalComplete')}
            data-testid={isAchieved ? TESTIDS.COACH_GOAL_REOPEN_BTN : TESTIDS.COACH_GOAL_COMPLETE_BTN}
            sx={{ color: isAchieved ? INTERAX_COLORS.thunderGrey : INTERAX_COLORS.green }}
          >
            {isAchieved ? <ArrowUturnLeftIcon width={16} /> : <CheckCircleIcon width={16} />}
          </IconButton>
          <IconButton
            size="small"
            onClick={startEditing}
            disabled={disabled}
            aria-label={t('gamer.ariaCoach.goalEdit')}
            data-testid={TESTIDS.COACH_GOAL_EDIT_BTN}
            sx={{ color: INTERAX_COLORS.thunderGrey }}
          >
            <PencilSquareIcon width={16} />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => onRequestDelete(goal)}
            disabled={disabled}
            aria-label={t('gamer.ariaCoach.goalDelete')}
            data-testid={TESTIDS.COACH_GOAL_DELETE_BTN}
            sx={{ color: INTERAX_COLORS.thunderGrey }}
          >
            <TrashIcon width={16} />
          </IconButton>
        </Stack>
      </Stack>
      {failureMessage && (
        <Typography
          role="alert"
          data-testid={TESTIDS.COACH_GOAL_FAILURE}
          sx={{ color: INTERAX_COLORS.redError, fontSize: 12, mt: 1 }}
        >
          {failureMessage}
        </Typography>
      )}
    </Box>
  )
}

const goalRowSx = { p: 1.5, borderRadius: 2, backgroundColor: INTERAX_COLORS.black20 }

/** A goal runs to about four lines in a 320px column before it is a paragraph rather than a goal. */
const GOAL_EDIT_MAX_ROWS = 4
